import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { TSP } from '../../../core/models';
import { COMPETENCE_KIND_LABELS } from '../competences.component';

export interface CompetenceFormData {
  competence?: TSP.Internal.Dictionary.Competence.AdminItem;
}

export type CompetenceFormResult =
  | { type: 'create'; payload: TSP.Internal.Dictionary.Competence.CreatePayload }
  | { type: 'update'; id: number; payload: TSP.Internal.Dictionary.Competence.UpdatePayload };

@Component({
  selector: 'app-competence-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
  ],
  templateUrl: './competence-form.html',
  styleUrl: './competence-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompetenceForm implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<CompetenceForm>);
  protected readonly data = inject<CompetenceFormData>(MAT_DIALOG_DATA);

  public readonly kindOptions = Object.values(TSP.Internal.Dictionary.Competence.Kind).map((kind) => ({
    value: kind,
    label: COMPETENCE_KIND_LABELS[kind],
  }));

  public form = new FormGroup({
    label: new FormControl<string | null>(null, [Validators.required]),
    kind: new FormControl<TSP.Internal.Dictionary.Competence.Kind>(TSP.Internal.Dictionary.Competence.Kind.OTHER, [
      Validators.required,
    ]),
    enName: new FormControl<string | null>(null),
    ruName: new FormControl<string | null>(null),
    ukName: new FormControl<string | null>(null),
  });

  public get isEditMode(): boolean {
    return !!this.data?.competence;
  }

  public ngOnInit(): void {
    if (this.data?.competence) {
      this.form.patchValue({
        label: this.data.competence.name,
        kind: this.data.competence.kind,
        enName: this.data.competence.enName,
        ruName: this.data.competence.ruName,
        ukName: this.data.competence.ukName,
      });
    }
  }

  public onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const label = raw.label as string;
    const kind = raw.kind as TSP.Internal.Dictionary.Competence.Kind;

    if (this.isEditMode) {
      // The three translation fields always travel: the form shows the stored
      // values, so a cleared input is an explicit delete (server: empty = drop).
      this.dialogRef.close({
        type: 'update',
        // isEditMode narrows data.competence, TS cannot see through the getter.
        id: (this.data.competence as TSP.Internal.Dictionary.Competence.AdminItem).id,
        payload: {
          label,
          kind,
          enName: raw.enName ?? '',
          ruName: raw.ruName ?? '',
          ukName: raw.ukName ?? '',
        },
      } satisfies CompetenceFormResult);
      return;
    }

    this.dialogRef.close({
      type: 'create',
      payload: {
        label,
        kind,
        enName: raw.enName || undefined,
        ruName: raw.ruName || undefined,
        ukName: raw.ukName || undefined,
      },
    } satisfies CompetenceFormResult);
  }

  public onCancel(): void {
    this.dialogRef.close(null);
  }
}
